import { useState, useCallback } from 'react';
import ContactService from '../services/contact.service';

/**
 * Hook personnalisé pour gérer le formulaire de contact
 * Suit le pattern des autres hooks du projet (useArticles, useCategories)
 */
const useContactForm = () => {
  const [formData, setFormData] = useState({
    type: 'demo',
    nom: '',
    email: '',
    societe: '',
    message: '',
    website: '' // Honeypot field
  });

  const [formState, setFormState] = useState({
    isSubmitting: false,
    isSubmitted: false,
    errors: {},
    message: '',
    messageType: 'success' // success | error
  });

  /**
   * Met à jour les données du formulaire
   */
  const updateFormData = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Effacer l'erreur pour ce champ
    if (formState.errors[field]) {
      setFormState(prev => ({
        ...prev,
        errors: {
          ...prev.errors,
          [field]: ''
        }
      }));
    }
  }, [formState.errors]);

  /**
   * Réinitialise le formulaire
   */
  const resetForm = useCallback(() => {
    setFormData({
      type: 'demo',
      nom: '',
      email: '',
      societe: '',
      message: '',
      website: ''
    });
    
    setFormState({
      isSubmitting: false,
      isSubmitted: false,
      errors: {},
      message: '',
      messageType: 'success'
    });
  }, []);

  /**
   * Valide le formulaire côté client
   */
  const validateForm = useCallback(() => {
    const errors = {};

    // Validation du nom
    if (!formData.nom.trim()) {
      errors.nom = 'Le nom est obligatoire';
    } else if (formData.nom.trim().length < 2) {
      errors.nom = 'Le nom doit contenir au moins 2 caractères';
    }

    // Validation de l'email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      errors.email = 'L\'email est obligatoire';
    } else if (!emailRegex.test(formData.email)) {
      errors.email = 'Format d\'email invalide';
    }

    // Validation du message
    if (!formData.message.trim()) {
      errors.message = 'Le message est obligatoire';
    } else if (formData.message.trim().length < 10) {
      errors.message = 'Le message doit contenir au moins 10 caractères';
    }

    // Vérification honeypot
    if (formData.website.trim() !== '') {
      errors.general = 'Requête suspecte détectée';
    }

    return errors;
  }, [formData]);

  /**
   * Soumet le formulaire
   */
  const submitForm = useCallback(async () => {
    // Validation du formulaire
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormState(prev => ({
        ...prev,
        errors,
        message: 'Veuillez corriger les erreurs dans le formulaire',
        messageType: 'error'
      }));
      return { success: false, errors };
    }

    setFormState(prev => ({
      ...prev,
      isSubmitting: true,
      errors: {},
      message: ''
    }));

    try {
      // Soumettre le formulaire
      const result = await ContactService.submitContactRequest(formData);

      if (result.success) {
        setFormState({
          isSubmitting: false,
          isSubmitted: true,
          errors: {},
          message: result.message,
          messageType: 'success'
        });

        // Réinitialiser les données du formulaire
        setFormData({
          type: 'demo',
          nom: '',
          email: '',
          societe: '',
          message: '',
          website: ''
        });

        return { success: true, data: result.data };
      } else {
        setFormState(prev => ({
          ...prev,
          isSubmitting: false,
          message: result.message,
          messageType: 'error'
        }));

        return { success: false, error: result.error };
      }
    } catch (error) {
      console.error('Erreur lors de la soumission:', error);
      
      setFormState(prev => ({
        ...prev,
        isSubmitting: false,
        message: 'Une erreur inattendue est survenue. Veuillez réessayer.',
        messageType: 'error'
      }));

      return { success: false, error: error.message };
    }
  }, [formData, validateForm]);

  /**
   * Réinitialise les états après succès
   */
  const resetFormState = useCallback(() => {
    setFormState({
      isSubmitting: false,
      isSubmitted: false,
      errors: {},
      message: '',
      messageType: 'success'
    });
  }, []);

  return {
    // Données
    formData,
    formState,
    
    // Actions
    updateFormData,
    submitForm,
    resetForm,
    resetFormState,
    
    // Utilitaires
    validateForm,
    
    // États dérivés
    isValid: Object.keys(validateForm()).length === 0,
    canSubmit: !formState.isSubmitting && !formState.isSubmitted
  };
};

export default useContactForm;
